import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { 
  User,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  reload,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  signInWithCustomToken
} from 'firebase/auth';
import { createUserProfile, getUserProfile, markUserAccessDevice, updateUserProfile } from '@/services/userProfileService';
import { NewsletterService } from '@/services/newsletterService';
import { deleteUserAccount as deleteUserAccountRequest } from '@/services/userService';
import { auth } from './firebase';
import { toast } from '@/hooks/use-toast';
import { isMobileWebView } from './mobileDetection';
import { getAccessDeviceInfo } from './deviceInfo';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { db } from './firebase';
import { doc as firestoreDoc, getDocFromServer } from 'firebase/firestore';

interface MobileUser {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  // Add provider information for Google user detection
  isGoogleUser?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: (shouldCreateProfile?: boolean, newsletterOptIn?: boolean) => Promise<User>;
  checkGoogleUserExists: (email: string) => Promise<boolean>;
  signInWithApple: (shouldCreateProfile?: boolean, newsletterOptIn?: boolean) => Promise<User>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, newsletterOptIn?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  isGoogleUser: () => boolean;
  sendPasswordReset: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  checkSignInMethods: (email: string) => Promise<{
    hasGoogleAuth: boolean;
    hasPasswordAuth: boolean;
    signInMethods: string[];
  } | null>;
  // Mobile auth properties
  isMobileAuthenticated: boolean;
  mobileUser: MobileUser | null;
  isAuthenticated: boolean; // Combined auth state
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileUser, setMobileUser] = useState<MobileUser | null>(null);
  const [isMobileAuthenticated, setIsMobileAuthenticated] = useState(false);
  const reportedDeviceRef = useRef<Set<string>>(new Set());
  const lastRecordedUidRef = useRef<string | null>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileStep, setProfileStep] = useState<0 | 1>(0);
  const [profileFirstName, setProfileFirstName] = useState('');
  const [profileLastName, setProfileLastName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileNotifs, setProfileNotifs] = useState({
    orderUpdates: true,
    promotions: true,
    newsletters: true
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const profileCheckRef = useRef(false);

  // Helper function to detect Google users from mobile auth data
  const detectGoogleUserFromMobileData = (authData: any): boolean => {
    // Strategy 1: Check for typical Google user indicators
    // Google users usually have displayName and/or photoURL populated
    const hasDisplayName = authData.displayName && authData.displayName !== 'null' && authData.displayName.trim() !== '';
    const hasPhotoURL = authData.photoURL && authData.photoURL !== 'null' && authData.photoURL.trim() !== '';
    
    // Google users typically have both name and photo, or at least one
    // Email/password users typically have neither
    const likelyGoogleUser = hasDisplayName || hasPhotoURL;
    
    
    return likelyGoogleUser;
  };

  // Create a virtual Firebase User object from mobile auth data
  const createVirtualUser = (mobileUserData: MobileUser): User => {
    // Create provider data based on detected authentication method
    const providerData = mobileUserData.isGoogleUser ? [{
      providerId: 'google.com',
      uid: mobileUserData.uid,
      displayName: mobileUserData.displayName,
      email: mobileUserData.email,
      phoneNumber: null,
      photoURL: mobileUserData.photoURL
    }] : [{
      providerId: 'password',
      uid: mobileUserData.uid,
      displayName: mobileUserData.displayName,
      email: mobileUserData.email,
      phoneNumber: null,
      photoURL: mobileUserData.photoURL
    }];

    return {
      uid: mobileUserData.uid,
      email: mobileUserData.email,
      emailVerified: true, // Always true since we're removing verification requirement
      displayName: mobileUserData.displayName,
      photoURL: mobileUserData.photoURL,
      // Add required properties that components might expect
      providerData: providerData,
      refreshToken: '',
      tenantId: null,
      isAnonymous: false,
      metadata: {
        creationTime: new Date().toISOString(),
        lastSignInTime: new Date().toISOString(),
      } as any,
      // Add stub methods that some components might call
      getIdToken: async () => sessionStorage.getItem('firebase-token') || '',
      reload: async () => {},
      delete: async () => {},
      getIdTokenResult: async () => ({} as any),
      toJSON: () => ({}),
    } as User;
  };

  // Create a unified user that combines Firebase and mobile auth
  const user = firebaseUser || (isMobileAuthenticated && mobileUser ? createVirtualUser(mobileUser) : null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUserFromAuth) => {
      setFirebaseUser(firebaseUserFromAuth);
      if (firebaseUserFromAuth?.uid) {
        recordAccessDevice(firebaseUserFromAuth.uid);
      } else {
        lastRecordedUidRef.current = null;
      }
      setLoading(false);
    });

    // Set up mobile auth handling
    if (isMobileWebView()) {
      initializeMobileAuth();
    }

    return () => unsubscribe();
  }, []);

  // Also cover virtual/mobile user sign-ins
  useEffect(() => {
    if (user?.uid) {
      recordAccessDevice(user.uid);
    }
  }, [user]);

  const recordAccessDevice = (uid: string) => {
    if (!uid) return;

    // Avoid double-capturing in the same session
    if (reportedDeviceRef.current.has(uid)) return;

    const sessionKey = `access_device_reported:v3:${uid}`;
    if (sessionStorage.getItem(sessionKey)) {
      reportedDeviceRef.current.add(uid);
      return;
    }

    // Prevent rapid double-fire for the same uid
    if (lastRecordedUidRef.current === uid) {
      return;
    }
    lastRecordedUidRef.current = uid;

    const deviceInfo = getAccessDeviceInfo();

    markUserAccessDevice(uid, deviceInfo)
      .then(() => {
        reportedDeviceRef.current.add(uid);
        sessionStorage.setItem(sessionKey, '1');
      })
      .catch((err) => {
        console.error('[AuthProvider] Failed to record access device', err);
      });
  };

  const initializeMobileAuth = () => {
    console.log('[AuthProvider] 🚀 Initializing mobile auth handling');
    console.log('[AuthProvider] Current URL:', window.location.href);
    console.log('[AuthProvider] Current localStorage keys:', Object.keys(localStorage));
    console.log('[AuthProvider] Current sessionStorage keys:', Object.keys(sessionStorage));
    
    // Listen for mobile auth events
    document.addEventListener('mobileAuthComplete', handleMobileAuthComplete);
    console.log('[AuthProvider] Event listener added for mobileAuthComplete');
    
    // Check for existing mobile auth data
    console.log('[AuthProvider] Checking for stored mobile auth data...');
    checkStoredMobileAuth();
    
    // Clean up on unmount
    return () => {
      console.log('[AuthProvider] Cleaning up mobile auth listeners');
      document.removeEventListener('mobileAuthComplete', handleMobileAuthComplete);
    };
  };

  const handleMobileAuthComplete = (event: any) => {
    console.log('[AuthProvider] Mobile auth complete event received');
    const authData = event.detail;

    // Only accept events when an explicit mobile auth flow is active
    const isActive = sessionStorage.getItem('mobile_auth_active');
    if (!isActive) {
      // If not an active flow, also guard against immediate re-auth after logout
    const recentLogoutSS = sessionStorage.getItem('recent_logout');
    const recentLogoutLS = localStorage.getItem('recent_logout');
    const now = Date.now();
    const isRecentLogout = ([recentLogoutSS, recentLogoutLS]
      .map(v => (v ? parseInt(v) : 0))
      .some(ts => Number.isFinite(ts) && (now - ts) < 5 * 60 * 1000));
    if (isRecentLogout) {
        console.log('[AuthProvider] Ignoring mobile auth due to recent logout (no active flow)');
      return;
    }
      console.log('[AuthProvider] Ignoring unsolicited mobile auth event (no active flow)');
      return;
    }

    processMobileAuthData(authData, false); // Don't show welcome message - AuthModalWrapper already handles this
  };

  const checkStoredMobileAuth = () => {
    try {
      console.log('[AuthProvider] 🔍 Checking stored mobile auth...');
      
      // Check for active mobile auth
      const isActive = sessionStorage.getItem('mobile_auth_active');
      const activeTs = sessionStorage.getItem('mobile_auth_active_ts');
      console.log('[AuthProvider] mobile_auth_active flag:', isActive);
      console.log('[AuthProvider] mobile_auth_active_ts:', activeTs);
      
      // If no active flag, continue to check stored auth (mobile app relaunch scenarios)
      if (!isActive) {
        console.log('[AuthProvider] No active mobile auth flag - proceeding to check stored auth');
      }
      
      if (activeTs) {
        const ts = parseInt(activeTs);
        if (Number.isFinite(ts)) {
          const ageMs = Date.now() - ts;
          if (ageMs > 2 * 60 * 1000) {
            console.log('[AuthProvider] mobile_auth_active flag is stale - clearing');
            sessionStorage.removeItem('mobile_auth_active');
            sessionStorage.removeItem('mobile_auth_active_ts');
            return;
          }
        }
      }

      const localStorageAuth = localStorage.getItem('jampackd_mobile_auth');
      const sessionStorageAuth = sessionStorage.getItem('jampackd_mobile_auth');
      console.log('[AuthProvider] localStorage auth data exists:', !!localStorageAuth);
      console.log('[AuthProvider] sessionStorage auth data exists:', !!sessionStorageAuth);
      
      const storedAuth = localStorageAuth || sessionStorageAuth;
      
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        console.log('[AuthProvider] Found stored mobile auth data for:', authData.email);
        console.log('[AuthProvider] Auth data timestamp:', authData.timestamp);
        
        // Check if data is still valid (within 1 hour)
        const authTime = new Date(authData.timestamp);
        const now = new Date();
        const diffMinutes = (now.getTime() - authTime.getTime()) / (1000 * 60);
        console.log('[AuthProvider] Auth data age (minutes):', diffMinutes);
        
        // Restore if stored auth data is fresh even if active flag is missing
        if (diffMinutes < 60) {
          console.log('[AuthProvider] Auth data is valid, processing...');
          processMobileAuthData(authData, false); // Don't show welcome message for restoration
        } else {
          console.log('[AuthProvider] ❌ Stored mobile auth data expired - clearing (without recent_logout)');
          // Clear stale mobile auth state without marking recent logout,
          // to avoid blocking subsequent mobile auth events
          try {
            setMobileUser(null);
            setIsMobileAuthenticated(false);
            localStorage.removeItem('jampackd_mobile_auth');
            sessionStorage.removeItem('jampackd_mobile_auth');
            sessionStorage.removeItem('mobile_auth_active');
            sessionStorage.removeItem('mobile_auth_active_ts');
          } catch (e) {
            console.error('[AuthProvider] Error clearing stale mobile auth:', e);
          }
        }
      } else {
        console.log('[AuthProvider] No stored auth data found despite active flag');
      }
    } catch (error) {
      console.error('[AuthProvider] ❌ Error checking stored mobile auth:', error);
    }
  };

  const processMobileAuthData = (authData: any, showWelcome: boolean = false) => {
    console.log('[AuthProvider] Processing mobile auth data for:', authData.email);
    console.log('[AuthProvider] Full auth data:', authData);
    console.log('[AuthProvider] Show welcome message:', showWelcome);
    
    // Detect if this is a Google user based on available data
    const isGoogleUser = detectGoogleUserFromMobileData(authData);
    
    const mobileUserData: MobileUser = {
      uid: authData.uid,
      email: authData.email,
      displayName: authData.displayName === 'null' ? null : authData.displayName,
      photoURL: authData.photoURL === 'null' ? null : authData.photoURL,
      isGoogleUser: isGoogleUser
    };
    
    console.log('[AuthProvider] Setting mobile user:', mobileUserData);
    console.log('[AuthProvider] Before state update - isMobileAuthenticated:', isMobileAuthenticated);
    console.log('[AuthProvider] Before state update - current user:', user);
    
    setMobileUser(mobileUserData);
    setIsMobileAuthenticated(true);
    
    console.log('[AuthProvider] Mobile auth state set - should create virtual user now');
    
    // Check the final state after a brief delay to account for React state updates
    setTimeout(() => {
      console.log('[AuthProvider] === POST-PROCESSING STATE CHECK ===');
      console.log('[AuthProvider] Final isMobileAuthenticated:', isMobileAuthenticated);
      console.log('[AuthProvider] Final mobileUser:', mobileUser);
      console.log('[AuthProvider] Final firebaseUser:', firebaseUser);
      console.log('[AuthProvider] Final computed user:', user);
      console.log('[AuthProvider] Final user exists:', !!user);
      console.log('[AuthProvider] Final user email:', user?.email);
      console.log('[AuthProvider] === END STATE CHECK ===');
    }, 500);
    
    // Only show welcome message for explicit authentication, not restoration
    if (showWelcome) {
      toast({
        title: "Welcome Back!",
        description: `Signed in as ${authData.email}`,
      });
    }
    
    console.log('[AuthProvider] Mobile authentication processing complete');
  };

  const clearMobileAuth = () => {
    console.log('[AuthProvider] ⚠️ CLEARING MOBILE AUTH - called from:', new Error().stack);
    setMobileUser(null);
    setIsMobileAuthenticated(false);
    localStorage.removeItem('jampackd_mobile_auth');
    sessionStorage.removeItem('jampackd_mobile_auth');
    sessionStorage.removeItem('mobile_auth_active');
    sessionStorage.removeItem('mobile_auth_active_ts');
    // Also set recent logout flag when clearing mobile auth
    const ts = Date.now().toString();
    sessionStorage.setItem('recent_logout', ts);
    localStorage.setItem('recent_logout', ts);
    console.log('[AuthProvider] Mobile auth cleared');
  };


  const checkGoogleUserExists = async (email: string): Promise<boolean> => {
    try {
      setError(null);
      
      // Use a temporary Google sign-in to get the user ID
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        login_hint: email,
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      
      if (result.user && result.user.email === email) {
        // Check if profile exists
        const userProfile = await getUserProfile(result.user.uid);
        
        // Sign out immediately since this was just a check
        await signOut(auth);
        
        return userProfile !== null;
      }
      
      // Sign out if email doesn't match
      await signOut(auth);
      return false;
    } catch (err) {
      console.error('Error checking Google user existence:', err);
      // If there's an error, we'll assume user doesn't exist
      return false;
    }
  };

  const signInWithGoogle = async (shouldCreateProfile: boolean = true, newsletterOptIn: boolean = true): Promise<User> => {
    try {
      setError(null);
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      if (!result.user) {
        throw new Error('No user returned from Google sign-in');
      }
      
      // Check if user profile exists
      const existingProfile = await getUserProfile(result.user.uid);
      
      if (!existingProfile && !shouldCreateProfile) {
        // User doesn't exist and we're not supposed to create profile
        await signOut(auth);
        throw new Error('USER_NOT_FOUND');
      }
      
      // Create user profile in Firestore if it doesn't exist and we should create it
      if (!existingProfile && shouldCreateProfile) {
        await createUserProfile(result.user.uid, {
          email: result.user.email,
          name: result.user.displayName || result.user.email?.split('@')[0] || 'User',
          notificationPreferences: {
            orderUpdates: true,
            promotions: true,
            newsletters: newsletterOptIn
          }
        });

        // Handle newsletter subscription if user opted in
        if (newsletterOptIn && result.user.email) {
          try {
            await NewsletterService.subscribe({
              email: result.user.email,
              source: 'google-registration'
            });
          } catch (newsletterError) {
            console.error('Newsletter subscription failed during Google registration:', newsletterError);
            // Don't fail registration if newsletter subscription fails
          }
        }
      }
      
      return result.user;
    } catch (err) {
      console.error('Error signing in with Google:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithApple = async (shouldCreateProfile: boolean = true, newsletterOptIn: boolean = true): Promise<User> => {
    try {
      setError(null);
      setLoading(true);
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const result = await signInWithPopup(auth, provider);

      if (!result.user) {
        throw new Error('No user returned from Apple sign-in');
      }

      const existingProfile = await getUserProfile(result.user.uid);

      if (!existingProfile && !shouldCreateProfile) {
        await signOut(auth);
        throw new Error('USER_NOT_FOUND');
      }

      if (!existingProfile && shouldCreateProfile) {
        await createUserProfile(result.user.uid, {
          email: result.user.email,
          name: result.user.displayName || result.user.email?.split('@')[0] || 'User',
          notificationPreferences: {
            orderUpdates: true,
            promotions: true,
            newsletters: newsletterOptIn
          }
        });

        if (newsletterOptIn && result.user.email) {
          try {
            await NewsletterService.subscribe({
              email: result.user.email,
              source: 'apple-registration'
            });
          } catch (newsletterError) {
            console.error('Newsletter subscription failed during Apple registration:', newsletterError);
          }
        }
      }

      return result.user;
    } catch (err) {
      console.error('Error signing in with Apple:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in with Apple');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error('Error signing in with email:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      throw err;
    }
  };

  const register = async (email: string, password: string, name: string, newsletterOptIn: boolean = true): Promise<void> => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile in Firestore using the service function
      if (result.user) {
        await createUserProfile(result.user.uid, {
          email: result.user.email,
          name,
          notificationPreferences: {
            orderUpdates: true,
            promotions: true,
            newsletters: newsletterOptIn
          }
        });

        // Handle newsletter subscription if user opted in
        if (newsletterOptIn && result.user.email) {
          try {
            await NewsletterService.subscribe({
              email: result.user.email,
              source: 'email-registration'
            });
          } catch (newsletterError) {
            console.error('Newsletter subscription failed during email registration:', newsletterError);
            // Don't fail registration if newsletter subscription fails
          }
        }
      }
    } catch (err) {
      console.error('Error registering:', err);
      setError(err instanceof Error ? err.message : 'Failed to register');
      throw err;
    }
  };

  const performLogout = async (options?: { silent?: boolean }) => {
    try {
      setError(null);

      // Set a flag to prevent unwanted auth modals immediately after logout
      sessionStorage.setItem('recent_logout', Date.now().toString());

      await signOut(auth);

      // Clear mobile auth state and cookies
      if (isMobileWebView()) {
        clearMobileAuth();
        document.cookie = 'firebase-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

        // Proactively instruct the mobile app to sign out its native session
        try {
          // This exists only inside the WebView environment
          (window as any).AuthChannel?.postMessage?.('logout');
          console.log('[AuthProvider] Sent logout message to mobile app');
        } catch (e) {
          console.warn('[AuthProvider] Failed to send logout message to mobile app:', e);
        }
      }

      if (!options?.silent) {
        toast({
          title: "Signed Out Successfully",
          description: "You have been signed out of your account. Thank you for visiting JamPack'd!",
        });
      }
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign out');

      toast({
        title: "Sign Out Failed",
        description: "There was an error signing you out. Please try again.",
        variant: "destructive",
      });

      throw err;
    }
  };

  const logout = async () => {
    await performLogout();
  };

  const getMobileWebViewToken = (): string | null => {
    if (typeof window === 'undefined') return null;

    try {
      const storedSession = sessionStorage.getItem('firebase-token');
      if (storedSession) {
        return storedSession;
      }

      const storedAuth = sessionStorage.getItem('jampackd_mobile_auth') ||
        localStorage.getItem('jampackd_mobile_auth');
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        if (parsed?.idToken && typeof parsed.idToken === 'string') {
          return parsed.idToken;
        }
      }

      const cookieMatch = document.cookie.match(/firebase-token=([^;]+)/);
      if (cookieMatch && cookieMatch[1]) {
        return decodeURIComponent(cookieMatch[1]);
      }
    } catch (error) {
      console.warn('Failed to read mobile auth token:', error);
    }

    return null;
  };

  const deleteAccount = async () => {
    if (!user) {
      throw new Error('You must be signed in to delete your account.');
    }

    try {
      setError(null);

      const idToken = firebaseUser
        ? await firebaseUser.getIdToken(true)
        : getMobileWebViewToken();

      if (!idToken) {
        throw new Error('Unable to verify your identity. Please sign out and sign back in, then try again.');
      }

      await deleteUserAccountRequest(idToken);
      await performLogout({ silent: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete account';
      console.error('Error deleting account:', err);
      setError(message);
      throw new Error(message);
    }
  };

  // Check if the current user is authenticated with Google
  const isGoogleUser = () => {
    if (!user) return false;
    
    // Check provider data (works for both Firebase users and virtual mobile users)
    return user.providerData.some(provider => provider.providerId === 'google.com');
  };


  const sendPasswordReset = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
      throw err;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setError(null);
      if (!firebaseUser) {
        throw new Error('Password change is only available for direct Firebase authentication');
      }
      if (!firebaseUser.email) {
        throw new Error('User email not available');
      }
      
      // Re-authenticate the user with their current password
      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      
      // Update to the new password
      await updatePassword(firebaseUser, newPassword);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err instanceof Error ? err.message : 'Failed to change password');
      throw err;
    }
  };

  const checkSignInMethods = async (email: string) => {
    try {
      setError(null);
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      // Check if Google is one of the sign-in methods
      const hasGoogleAuth = signInMethods.includes('google.com');
      // Check if email/password is one of the sign-in methods
      const hasPasswordAuth = signInMethods.includes('password');
      
      return {
        hasGoogleAuth,
        hasPasswordAuth,
        signInMethods
      };
    } catch (err) {
      console.error('Error checking sign-in methods:', err);
      setError(err instanceof Error ? err.message : 'Failed to check sign-in methods');
      return null;
    }
  };

  // Combined authentication state
  const isAuthenticated = user !== null;
  
  // Check profile completeness on auth changes
  useEffect(() => {
    const run = async () => {
      if (!user) {
        setProfileModalOpen(false);
        return;
      }

      const sessionKey = `profile_checked:v2:${user.uid}`;
      if (sessionStorage.getItem(sessionKey)) {
        return;
      }

      if (profileCheckRef.current) return;
      profileCheckRef.current = true;

      try {
        let profile = await getUserProfile(user.uid);
        // If incomplete, try fetching fresh from server (avoid stale cache)
        const hasNameCached = !!profile?.name && profile.name.trim().length > 0;
        const hasEmailCached = !!profile?.email;
        const hasNotifsCached = !!profile?.notificationPreferences;
        if (!profile || !hasNameCached || !hasEmailCached || !hasNotifsCached) {
          try {
            const freshSnap = await getDocFromServer(firestoreDoc(db, 'users', user.uid));
            if (freshSnap.exists()) {
              profile = { uid: freshSnap.id, ...(freshSnap.data() as any) };
            }
          } catch (serverErr) {
            console.warn('[AuthProvider] Unable to fetch fresh profile from server', serverErr);
          }
        }

        const hasName = !!profile?.name && profile.name.trim().length > 0;
        const hasEmail = !!profile?.email;
        const hasNotifs = !!profile?.notificationPreferences;

        if (!profile || !hasName || !hasEmail || !hasNotifs) {
          // Do not clobber fields if the modal is already open (user may be typing)
          if (!profileModalOpen) {
            const existingName = (profile?.name || user.displayName || '').trim();
            const nameParts = existingName.length ? existingName.split(/\s+/) : [];
            const first = nameParts[0] || '';
            const last = nameParts.slice(1).join(' ');

            setProfileFirstName(first);
            setProfileLastName(last);
            setProfileEmail(profile?.email || user.email || '');
            setProfileNotifs(profile?.notificationPreferences || {
              orderUpdates: true,
              promotions: true,
              newsletters: true
            });
            setProfileStep(0);
          }
          setProfileModalOpen(true);
        } else {
          sessionStorage.setItem(sessionKey, '1');
          setProfileModalOpen(false);
        }
      } catch (err) {
        console.error('[AuthProvider] Failed to check profile completeness', err);
      } finally {
        profileCheckRef.current = false;
      }
    };

    run();
  }, [user]);

  const handleProfileNext = () => {
    if (!profileFirstName.trim() || !profileLastName.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter both first and last name to continue.',
        variant: 'destructive'
      });
      return;
    }
    setProfileStep(1);
  };

  const handleProfileBack = () => setProfileStep(0);

  const handleProfileSave = async () => {
    if (!user) return;
    if (!profileFirstName.trim() || !profileLastName.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter both first and last name to continue.',
        variant: 'destructive'
      });
      return;
    }

    setProfileSaving(true);
    const name = `${profileFirstName.trim()} ${profileLastName.trim()}`.trim();
    const payload = {
      email: profileEmail || user.email || null,
      name,
      notificationPreferences: profileNotifs
    };

    try {
      const existing = await getUserProfile(user.uid);
      if (existing) {
        await updateUserProfile(user.uid, payload);
      } else {
        await createUserProfile(user.uid, payload);
      }
      toast({
        title: 'Profile updated',
        description: 'Thanks for confirming your details.'
      });
      const sessionKey = `profile_checked:v2:${user.uid}`;
      sessionStorage.setItem(sessionKey, '1');
      setProfileModalOpen(false);
    } catch (err) {
      console.error('[AuthProvider] Failed to save profile', err);
      toast({
        title: 'Save failed',
        description: 'Please try again in a moment.',
        variant: 'destructive'
      });
    } finally {
      setProfileSaving(false);
    }
  };


  return (
    <AuthContext.Provider 
      value={{
        user,
        loading,
        error,
        signInWithGoogle,
        signInWithApple,
        checkGoogleUserExists,
        signInWithEmail,
        register,
        logout,
        deleteAccount,
        isGoogleUser,
        sendPasswordReset,
        changePassword,
        checkSignInMethods,
        // Keep mobile auth properties for compatibility
        isMobileAuthenticated,
        mobileUser,
        isAuthenticated
      }}
    >
      {children}
      <Dialog open={profileModalOpen} onOpenChange={(open) => setProfileModalOpen(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete your profile</DialogTitle>
          </DialogHeader>

          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              We couldn&apos;t finish updating your profile. Please confirm your details to continue.
            </AlertDescription>
          </Alert>

          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${profileStep === 0 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}>1</div>
              <span>Personal info</span>
            </div>
            <div className="h-px flex-1 bg-gray-200" />
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${profileStep === 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}>2</div>
              <span>Notifications</span>
            </div>
          </div>

          {profileStep === 0 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profile-first-name">First Name</Label>
                <Input
                  id="profile-first-name"
                  value={profileFirstName}
                  onChange={(e) => setProfileFirstName(e.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-last-name">Last Name</Label>
                <Input
                  id="profile-last-name"
                  value={profileLastName}
                  onChange={(e) => setProfileLastName(e.target.value)}
                  placeholder="Last name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  value={profileEmail}
                  disabled
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Choose how you want to hear from us.</p>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">Order updates</p>
                  <p className="text-xs text-gray-600">Get notified about your orders.</p>
                </div>
                <Switch
                  checked={profileNotifs.orderUpdates}
                  onCheckedChange={(val) => setProfileNotifs((p) => ({ ...p, orderUpdates: val }))}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">Promotions</p>
                  <p className="text-xs text-gray-600">Deals, offers, and member perks.</p>
                </div>
                <Switch
                  checked={profileNotifs.promotions}
                  onCheckedChange={(val) => setProfileNotifs((p) => ({ ...p, promotions: val }))}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">Newsletters</p>
                  <p className="text-xs text-gray-600">Product news and community updates.</p>
                </div>
                <Switch
                  checked={profileNotifs.newsletters}
                  onCheckedChange={(val) => setProfileNotifs((p) => ({ ...p, newsletters: val }))}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            {profileStep === 1 && (
              <Button variant="outline" onClick={handleProfileBack} disabled={profileSaving}>
                Back
              </Button>
            )}
            {profileStep === 0 ? (
              <Button onClick={handleProfileNext} disabled={profileSaving}>
                Next
              </Button>
            ) : (
              <Button onClick={handleProfileSave} disabled={profileSaving}>
                {profileSaving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
}; 
